// @ts-nocheck
// TODO: tighten types after migration
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FiBell, FiPlus } from "react-icons/fi";
import { HiChevronDown } from "react-icons/hi";
import CommentForm from "./CommentForm";
import CommentItem from "./CommentItem";
import { CommentCurrentUser, CommentEntityType, CommentRecord, CommentsAdapter } from "./types";
import { getAuthUser, getAuthToken } from "../../utils/auth";

type SortOrder = "newest" | "oldest";

type CommentsPanelProps = {
  entityType: CommentEntityType;
  entityId: string;
  adapter: CommentsAdapter;
  canComment: boolean;
  currentUser?: CommentCurrentUser | null;
  onLoginRequired?: () => void;
  className?: string;
  variant?: "blog" | "podcast";
  density?: "regular" | "compact";
  pollingIntervalMs?: number;
};

function safeParseUser(raw: string | null): CommentCurrentUser | null {
  if (!raw || raw === "undefined" || raw === "null") {
    return null;
  }

  try {
    return JSON.parse(raw) as CommentCurrentUser;
  } catch {
    return null;
  }
}

function formatTimestamp(timestamp: string): string {
  const now = new Date();
  const commentDate = new Date(timestamp);
  const diffMs = now.getTime() - commentDate.getTime();
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));

  if (diffHours < 24) {
    return diffHours === 0 ? "Just now" : `${diffHours} hr${diffHours > 1 ? "s" : ""} ago`;
  }

  const day = String(commentDate.getDate()).padStart(2, "0");
  const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const month = monthNames[commentDate.getMonth()];
  const year = String(commentDate.getFullYear());

  let hours = commentDate.getHours();
  const minutes = String(commentDate.getMinutes()).padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12 || 12;

  return `${day} ${month} ${year}, ${hours}:${minutes} ${ampm}`;
}

export default function CommentsPanel(props: CommentsPanelProps) {
  const {
    entityType,
    entityId,
    adapter,
    canComment,
    currentUser,
    onLoginRequired,
    className,
    variant = "blog",
    pollingIntervalMs = 5000,
  } = props;

  void entityType;

  const navigate = useNavigate();
  const location = useLocation();

  const [commentText, setCommentText] = useState("");
  const [comments, setComments] = useState<CommentRecord[]>([]);
  const [replyTo, setReplyTo] = useState<string | null>(null);
  const [isPosting, setIsPosting] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [sortOrder, setSortOrder] = useState<SortOrder>("newest");
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  const [reportCommentId, setReportCommentId] = useState<string | null>(null);
  const [reportMessage, setReportMessage] = useState("");
  const [isReporting, setIsReporting] = useState(false);
  const [actionStatus, setActionStatus] = useState<Record<string, "loading" | "success" | undefined>>({});
  const [shakeBtn, setShakeBtn] = useState(false);

  const inputRef = useRef<HTMLTextAreaElement>(null);
  const pickerRef = useRef<HTMLDivElement>(null);

  const [sessionUser, setSessionUser] = useState<CommentCurrentUser | null>(() => getAuthUser() as CommentCurrentUser | null);
  const [sessionToken, setSessionToken] = useState<string | null>(() => getAuthToken());

  useEffect(() => {
    const handleUpdate = () => {
      setSessionUser(getAuthUser() as CommentCurrentUser | null);
      setSessionToken(getAuthToken());
    };
    window.addEventListener("userUpdated", handleUpdate);
    window.addEventListener("storage", handleUpdate); // For multi-tab support
    return () => {
      window.removeEventListener("userUpdated", handleUpdate);
      window.removeEventListener("storage", handleUpdate);
    };
  }, []);

  const resolvedUser = useMemo(() => currentUser ?? sessionUser, [currentUser, sessionUser]);
  const canInteract = canComment && Boolean(resolvedUser && sessionToken);

  useEffect(() => {
    let intervalId: ReturnType<typeof setInterval> | undefined;
    let isMounted = true;

    const fetchComments = async () => {
      try {
        const data = await adapter.fetchComments();
        if (isMounted) {
          setComments(Array.isArray(data) ? data : []);
        }
      } catch (error) {
        if (isMounted) {
          setComments([]);
        }
      }
    };

    fetchComments();
    intervalId = setInterval(fetchComments, pollingIntervalMs);

    return () => {
      isMounted = false;
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [adapter, pollingIntervalMs, entityId]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (pickerRef.current && !pickerRef.current.contains(target)) {
        setShowEmojiPicker(false);
      }
      if (!target.closest(".comment-more-icon")) {
        setActiveMenu(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const goToAuth = () => {
    if (onLoginRequired) {
      onLoginRequired();
      return;
    }
    navigate("/signin", { state: { from: location } });
  };

  const goToSignup = () => navigate("/signin");

  const onEmojiClick = (emoji: string) => {
    if (!inputRef.current) {
      return;
    }
    const start = inputRef.current.selectionStart;
    const end = inputRef.current.selectionEnd;
    const text = commentText;
    setCommentText(`${text.slice(0, start)}${emoji}${text.slice(end)}`);

    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.selectionStart = start + emoji.length;
        inputRef.current.selectionEnd = start + emoji.length;
        inputRef.current.focus();
      }
    }, 0);

    setShowEmojiPicker(false);
  };

  const handleDeleteComment = async (commentId: string) => {
    if (!resolvedUser) {
      return;
    }

    if (!window.confirm("Are you sure you want to delete this comment? This will also remove any replies.")) {
      return;
    }

    setActionStatus((previous) => ({ ...previous, [commentId]: "loading" }));

    try {
      await adapter.deleteComment(commentId, resolvedUser._id);
      setActionStatus((previous) => ({ ...previous, [commentId]: "success" }));

      setTimeout(() => {
        setComments((previous) => previous.filter((item) => item._id !== commentId && item.nestedId !== commentId));
        setActionStatus((previous) => {
          const next = { ...previous };
          delete next[commentId];
          return next;
        });
        setActiveMenu(null);
      }, 1000);
    } catch (error) {
      setActionStatus((previous) => {
        const next = { ...previous };
        delete next[commentId];
        return next;
      });
      alert("Failed to delete comment");
    }
  };

  const handleReportSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!reportCommentId || !resolvedUser || !reportMessage.trim()) {
      return;
    }

    setIsReporting(true);
    setActionStatus((previous) => ({ ...previous, [reportCommentId]: "loading" }));

    try {
      await adapter.reportComment(reportCommentId, { userId: resolvedUser._id, message: reportMessage });
      setActionStatus((previous) => ({ ...previous, [reportCommentId]: "success" }));

      setTimeout(() => {
        setReportCommentId(null);
        setReportMessage("");
        setIsReporting(false);
        setActionStatus((previous) => {
          const next = { ...previous };
          delete next[reportCommentId];
          return next;
        });
      }, 1200);
    } catch (error) {
      setIsReporting(false);
      setActionStatus((previous) => {
        const next = { ...previous };
        delete next[reportCommentId];
        return next;
      });
      alert("Failed to submit report");
    }
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!canInteract) {
      goToAuth();
      return;
    }

    if (!commentText.trim()) {
      return;
    }

    setIsPosting(true);

    try {
      const created = await adapter.postComment({
        userId: resolvedUser._id,
        commentText,
        nestedId: replyTo,
      });
      setCommentText("");
      setReplyTo(null);
      setComments((previous) => [created, ...previous]);
    } catch (error) {
      alert("Couldn't post comment");
    } finally {
      setIsPosting(false);
    }
  };

  const renderComments = (parentId: string | null = null, level = 0): React.ReactNode[] => {
    const filtered = comments.filter((item) => item.nestedId === parentId);
    if (filtered.length === 0) {
      return [];
    }

    const sorted = [...filtered].sort((a, b) => {
      const first = new Date(a.timestamp).getTime();
      const second = new Date(b.timestamp).getTime();
      return sortOrder === "newest" ? second - first : first - second;
    });

    const displayList = isExpanded ? sorted : sorted.slice(0, 5);

    return displayList.map((item, index) => {
      const children = renderComments(item._id, 1);
      return (
        <React.Fragment key={item._id}>
          <CommentItem
            comment={item}
            level={level}
            user={resolvedUser}
            activeMenu={activeMenu}
            actionStatus={actionStatus}
            onToggleMenu={(id) => setActiveMenu(activeMenu === id ? null : id)}
            onDeleteComment={handleDeleteComment}
            onReportComment={(id) => {
              if (!resolvedUser) {
                goToAuth();
                return;
              }
              setReportCommentId(id);
              setActiveMenu(null);
            }}
            onSetReplyTo={setReplyTo}
            formatTimestamp={formatTimestamp}
          />
          {level === 0 && children.length > 0 && <div className="replies-wrapper">{children}</div>}
          {level === 0 && index < displayList.length - 1 && <hr className="comment-separator" />}
        </React.Fragment>
      );
    });
  };

  const wrapperClass = variant === "blog"
    ? "blog-thoughts-wrapper relative z-[10] m-[2rem_0_0_0] bg-bg p-[0.5rem] text-text"
    : "podcast-thoughts-wrapper";

  return (
    <div className={`${wrapperClass}${className ? ` ${className}` : ""}`}>
      <div className={variant === "blog" ? "comments-section-header mb-[0.5rem]" : "comments-section-header"}>
        <div className={variant === "blog" ? "comments-title-container flex items-center gap-[1.5rem]" : "comments-title-container"}>
          <h2
            className={variant === "blog" ? "comments-main-title relative m-0 text-[2.8rem] font-[800] tracking-[-0.01em] text-[var(--text-color)]" : "comments-main-title"}
            data-text="Comments"
          >
            Comments
          </h2>
          <div className={variant === "blog" ? "comments-header-line h-[2px] flex-1 bg-[#47b5ff]" : "comments-header-line"}></div>
        </div>

        <div className={variant === "blog" ? "conversation-stats-row mb-[0.1rem] flex items-center justify-between py-[0.75rem]" : "conversation-stats-row"}>
          <div className={variant === "blog" ? "stats-left flex items-center gap-[0.5rem]" : "stats-left"}>
            <span className={variant === "blog" ? "convo-label text-[1.1rem] font-[700]" : "convo-label"}>Conversation</span>
            <span className={variant === "blog" ? "convo-count text-[0.85rem] text-[#888]" : "convo-count"}>{comments.length} Comments</span>
          </div>
          <div className={variant === "blog" ? "stats-right flex items-center" : "stats-right"}>
            <div
              className={variant === "blog" ? "sort-feature flex cursor-pointer items-center gap-[4px] text-[0.85rem] text-[#888] transition-colors duration-200 hover:text-white" : "sort-feature"}
              onClick={() => setSortOrder(sortOrder === "newest" ? "oldest" : "newest")}
            >
              <span className="sort-text">Sort by <strong>{sortOrder === "newest" ? "Newest" : "Oldest"}</strong></span>
              <HiChevronDown className={`sort-icon ${sortOrder === "oldest" ? "rotated" : ""} ${variant === "blog" && sortOrder === "oldest" ? "rotate-180" : ""}`} />
            </div>
          </div>
        </div>

        {!canInteract && (
          <div className={variant === "blog" ? "comments-auth-container mt-[0.5rem] flex items-center justify-end gap-[1rem]" : "comments-auth-container"}>
            <FiBell className={variant === "blog" ? "bell-icon cursor-pointer text-[#888]" : "bell-icon"} />
            <div className={variant === "blog" ? "auth-links flex gap-[0.5rem] text-[0.9rem] font-[500]" : "auth-links"}>
              <span className={variant === "blog" ? "auth-link cursor-pointer text-[#aaa] transition-colors duration-200 hover:text-white hover:underline" : "auth-link"} onClick={goToAuth}>Log in</span>
              <span className={variant === "blog" ? "auth-sep text-[#444]" : "auth-sep"}>|</span>
              <span className={variant === "blog" ? "auth-link cursor-pointer text-[#aaa] transition-colors duration-200 hover:text-white hover:underline" : "auth-link"} onClick={goToSignup}>Sign up</span>
            </div>
          </div>
        )}
      </div>

      <CommentForm
        variant={variant}
        commentText={commentText}
        setCommentText={setCommentText}
        replyTo={replyTo}
        setReplyTo={setReplyTo}
        isPosting={isPosting}
        canComment={canInteract}
        showTooltip={showTooltip}
        setShowTooltip={setShowTooltip}
        onUnauthenticatedFocus={() => {
          if (!canInteract) {
            setShakeBtn(true);
            setTimeout(() => setShakeBtn(false), 500);
          }
        }}
        showEmojiPicker={showEmojiPicker}
        setShowEmojiPicker={setShowEmojiPicker}
        onSubmit={handleSubmit}
        onEmojiClick={onEmojiClick}
        onLoginClick={goToAuth}
        onSignupClick={goToSignup}
        inputRef={inputRef}
        pickerRef={pickerRef}
      />

      <div className="comments-list-container">
        <div className={variant === "blog" ? "comments-list-viewport relative" : "comments-list-viewport"}>
          {!canInteract && comments.length > 0 && (
            <div className={variant === "blog" ? "comments-blur-overlay absolute inset-0 z-[10] flex items-center justify-center" : "comments-blur-overlay"}>
              <div className={`auth-prompt-center ${shakeBtn ? "shake-pulse" : ""}${variant === "blog" ? " rounded-[12px] bg-black/55 p-[16px] text-center backdrop-blur-sm" : ""}`}>
                <button
                  onClick={goToAuth}
                  className={variant === "blog" ? "login-view-btn rounded-[8px] border-none bg-[#47b5ff] px-[14px] py-[8px] font-[600] text-white" : "login-view-btn"}
                >
                  Login / Signup
                </button>
                <p className={variant === "blog" ? "auth-helper-text mt-[8px] text-[0.85rem] text-white/90" : "auth-helper-text"}>Please login to view or post comments</p>
              </div>
            </div>
          )}

          <div className={`comments-list ${!canInteract ? "is-blurred" : ""}`}>{renderComments()}</div>
        </div>

        {!isExpanded && comments.length > 5 && (
          <div className={variant === "blog" ? "expand-container mt-[12px] flex justify-center" : "expand-container"}>
            <button
              className={variant === "blog" ? "expand-btn flex items-center gap-[6px] rounded-[10px] border border-slider bg-bg px-[14px] py-[8px] text-[0.85rem] font-[700] text-text" : "expand-btn"}
              onClick={() => setIsExpanded(true)}
            >
              <FiPlus className="expand-plus" />
              <span>EXPAND</span>
            </button>
          </div>
        )}
      </div>

      {reportCommentId && (
        <div className={variant === "blog" ? "report-overlay fixed inset-0 z-[1000] flex items-center justify-center bg-black/60 p-[16px]" : "report-overlay"}>
          <div className={variant === "blog" ? "report-modal w-full max-w-[520px] rounded-[12px] bg-bg p-[16px] shadow-[0_18px_40px_rgba(0,0,0,0.35)]" : "report-modal"}>
            <h3>Report Comment</h3>
            <p>What's wrong with this comment? Let us know why it should be reviewed.</p>
            <form onSubmit={handleReportSubmit}>
              <textarea
                className={variant === "blog" ? "report-textarea min-h-[120px] w-full resize-y rounded-[8px] border border-slider bg-transparent p-[10px] text-text outline-none focus:border-[#47b5ff]" : "report-textarea"}
                placeholder="Enter your complaint..."
                value={reportMessage}
                onChange={(event) => setReportMessage(event.target.value)}
                required
                autoFocus
              />
              <div className={variant === "blog" ? "report-actions mt-[10px] flex justify-end gap-[8px]" : "report-actions"}>
                <button
                  type="button"
                  className={variant === "blog" ? "report-btn btn-cancel rounded-[8px] border border-slider bg-transparent px-[12px] py-[8px] text-text" : "report-btn btn-cancel"}
                  onClick={() => setReportCommentId(null)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className={`${variant === "blog" ? "report-btn btn-submit rounded-[8px] border-none bg-[#47b5ff] px-[12px] py-[8px] text-white" : "report-btn btn-submit"} ${actionStatus[reportCommentId] ? `status-${actionStatus[reportCommentId]}` : ""}`}
                  disabled={isReporting}
                >
                  {actionStatus[reportCommentId] === "loading"
                    ? "Reporting..."
                    : actionStatus[reportCommentId] === "success"
                      ? "Reported"
                      : "Submit Report"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
